import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle,
    CheckCircle2,
    HelpCircle,
    Info,
    ListChecks,
    Loader2,
    RefreshCw,
    ShieldCheck,
    XCircle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ordersApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

/**
 * TracklistValidationCard
 *
 * Surface DDP × booklet validation results on the order file page. Only
 * shown for CD orders. Contains three interaction points:
 *   1. "Re-run validation" button (any authenticated user on the order).
 *   2. Admin-only "Acknowledge / Override" flow with a free-text note.
 *   3. Per-disc manual mapping: when auto-assignment cannot link a booklet
 *      section to a disc, admins/customers can pick the correct section
 *      from a dropdown and the validator re-runs.
 *
 * The card degrades gracefully: if the order has no CD product type or the
 * backend returns not_run, it collapses to a compact info banner.
 */

// ─── Verdict / status styling ──────────────────────────────────────────────

const OVERALL_STYLES = {
    pass: {
        label: 'Match',
        badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        icon: CheckCircle2,
        iconClass: 'text-emerald-600',
    },
    warning: {
        label: 'Needs review',
        badgeClass: 'bg-amber-100 text-amber-800 border-amber-200',
        icon: AlertTriangle,
        iconClass: 'text-amber-600',
    },
    fail: {
        label: 'Mismatch',
        badgeClass: 'bg-red-100 text-red-800 border-red-200',
        icon: XCircle,
        iconClass: 'text-red-600',
    },
    not_run: {
        label: 'Not yet run',
        badgeClass: 'bg-slate-100 text-slate-700 border-slate-200',
        icon: Info,
        iconClass: 'text-slate-500',
    },
};

const DISC_STYLES = {
    match: {
        label: 'Match',
        chipClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        icon: CheckCircle2,
    },
    track_count_mismatch: {
        label: 'Track count differs',
        chipClass: 'bg-red-50 text-red-700 border-red-200',
        icon: XCircle,
    },
    titles_differ: {
        label: 'Titles differ',
        chipClass: 'bg-red-50 text-red-700 border-red-200',
        icon: XCircle,
    },
    titles_review: {
        label: 'Titles need review',
        chipClass: 'bg-amber-50 text-amber-800 border-amber-200',
        icon: AlertTriangle,
    },
    titles_unavailable: {
        label: 'Track count matches (titles n/a)',
        chipClass: 'bg-sky-50 text-sky-700 border-sky-200',
        icon: Info,
    },
    needs_manual_mapping: {
        label: 'Manual mapping needed',
        chipClass: 'bg-amber-50 text-amber-800 border-amber-200',
        icon: HelpCircle,
    },
};

const getOverallStyle = (status) => OVERALL_STYLES[status] || OVERALL_STYLES.not_run;
const getDiscStyle = (status) => DISC_STYLES[status] || DISC_STYLES.needs_manual_mapping;

// ─── Small building blocks ────────────────────────────────────────────────

const OverallBanner = ({ status, message, ackInfo }) => {
    const style = getOverallStyle(status);
    const Icon = style.icon;
    // Only surface the override badge when there's an active mismatch/warning.
    // If the underlying files have changed and validation now passes or is
    // not_run, an old ack shouldn't visually persist.
    const acknowledged = (status === 'fail' || status === 'warning') && ackInfo?.acknowledged;
    return (
        <div
            className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${style.badgeClass}`}
            data-testid="tracklist-overall-banner"
        >
            <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${style.iconClass}`} />
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold" data-testid="tracklist-overall-status">
                        {style.label}
                    </span>
                    {acknowledged && (
                        <span
                            className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2 py-0.5 text-xs font-medium text-slate-700"
                            data-testid="tracklist-ack-badge"
                        >
                            <ShieldCheck className="h-3 w-3" />
                            Overridden by {ackInfo.by_name}
                        </span>
                    )}
                </div>
                <p className="text-sm mt-0.5 leading-snug">{message}</p>
                {acknowledged && ackInfo.notes && (
                    <p className="text-xs mt-1 text-slate-600 italic">
                        Note: {ackInfo.notes}
                    </p>
                )}
            </div>
        </div>
    );
};

const DiscMappingSelector = ({
    disc,
    bookletSections,
    manualMapping,
    onMap,
    disabled,
}) => {
    // Determine which section is currently in play for this disc:
    //  - explicit manual mapping wins
    //  - otherwise the auto-assigned section (heuristic: match by heading)
    const currentMappedIndex = useMemo(() => {
        if (manualMapping && manualMapping[String(disc.disc_number)] != null) {
            return Number(manualMapping[String(disc.disc_number)]);
        }
        if (disc.booklet_section_heading == null) return null;
        const match = bookletSections.findIndex(
            (s) => s.heading === disc.booklet_section_heading,
        );
        return match >= 0 ? match : null;
    }, [manualMapping, disc, bookletSections]);

    if (!bookletSections || bookletSections.length === 0) return null;

    return (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
            <label className="text-xs text-slate-500">
                Booklet section:
            </label>
            <select
                className="text-xs border border-slate-300 rounded px-2 py-1 bg-white disabled:opacity-60"
                value={currentMappedIndex ?? ''}
                onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') return;
                    onMap(disc.disc_number, Number(v));
                }}
                disabled={disabled}
                data-testid={`tracklist-map-select-disc-${disc.disc_number}`}
            >
                <option value="">— pick a section —</option>
                {bookletSections.map((s) => (
                    <option key={s.index} value={s.index}>
                        {s.heading || `Section ${s.index + 1}`} ({s.track_count} tracks
                        {s.confidence ? `, ${s.confidence} confidence` : ''})
                    </option>
                ))}
            </select>
        </div>
    );
};

const DiscRow = ({
    disc,
    bookletSections,
    manualMapping,
    onMap,
    canRemap,
    onApprove,
    onRevokeApproval,
    canApprove,
    busy,
}) => {
    const style = getDiscStyle(disc.verdict?.status);
    const Icon = style.icon;
    const showMappingSelector =
        disc.verdict?.status === 'needs_manual_mapping' && bookletSections.length > 0;

    // A disc is "mismatched" (i.e. approvable) whenever the verdict is
    // anything other than a clean match. Excludes `needs_manual_mapping`
    // because there's nothing to approve until the mapping is settled.
    const mismatchStatuses = ['titles_differ', 'titles_review', 'track_count_mismatch'];
    const isMismatch = mismatchStatuses.includes(disc.verdict?.status);
    const ack = disc.disc_ack;
    const isApproved = !!(ack && ack.approved);

    // Local approve-form state so multiple discs can be open independently.
    const [approving, setApproving] = useState(false);
    const [notes, setNotes] = useState('');

    const doApprove = async () => {
        await onApprove?.(disc.disc_number, notes.trim());
        setApproving(false);
        setNotes('');
    };

    return (
        <div
            className={`rounded-lg border p-3 ${isApproved ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-white'}`}
            data-testid={`tracklist-disc-row-${disc.disc_number}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900">
                            {disc.label || `Disc ${disc.disc_number}`}
                        </span>
                        <span
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${style.chipClass}`}
                            data-testid={`tracklist-disc-status-${disc.disc_number}`}
                        >
                            <Icon className="h-3 w-3" />
                            {style.label}
                        </span>
                        {isApproved && (
                            <span
                                className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800"
                                data-testid={`tracklist-disc-approved-${disc.disc_number}`}
                                title={ack.notes ? `Note: ${ack.notes}` : undefined}
                            >
                                <ShieldCheck className="h-3 w-3" />
                                Approved by {ack.by_name}
                                {ack.by_role === 'customer' ? ' (customer)' : ''}
                            </span>
                        )}
                        {!disc.has_ddp && (
                            <span className="text-xs text-slate-500 italic">
                                No DDP uploaded yet
                            </span>
                        )}
                    </div>
                    {disc.verdict?.message && (
                        <p className="text-xs text-slate-600 mt-1 leading-snug">
                            {disc.verdict.message}
                        </p>
                    )}
                    {(disc.ddp_track_count != null || disc.verdict?.ddp_track_count != null) && (
                        <div className="mt-1 flex gap-4 text-xs text-slate-500 flex-wrap">
                            <span>
                                DDP tracks:{' '}
                                <span className="text-slate-800 font-medium">
                                    {disc.verdict?.ddp_track_count ?? '—'}
                                </span>
                            </span>
                            <span>
                                Booklet tracks:{' '}
                                <span className="text-slate-800 font-medium">
                                    {disc.verdict?.booklet_track_count ?? '—'}
                                </span>
                            </span>
                            {disc.ddp_filename && (
                                <span className="truncate max-w-[220px]" title={disc.ddp_filename}>
                                    File: {disc.ddp_filename}
                                </span>
                            )}
                            {disc.artwork_filename && (
                                <span
                                    className="truncate max-w-[240px]"
                                    title={disc.artwork_filename}
                                    data-testid={`tracklist-disc-artwork-file-${disc.disc_number}`}
                                >
                                    Artwork: {disc.artwork_filename}
                                </span>
                            )}
                        </div>
                    )}
                    {disc.ddp_extraction_failed && (
                        <p
                            className="text-xs text-amber-700 mt-1 leading-snug"
                            data-testid={`tracklist-disc-ddp-extraction-failed-${disc.disc_number}`}
                        >
                            Could not read the track list from this disc's DDP master
                            (PQDESCR missing or unreadable) — this is an extraction
                            issue, not a confirmed empty disc. Verify the uploaded
                            master before relying on the count.
                        </p>
                    )}
                    {disc.verdict?.differences?.length > 0 && (
                        <details className="mt-2" data-testid={`tracklist-disc-diff-${disc.disc_number}`}>
                            <summary className="text-xs text-slate-700 cursor-pointer hover:text-slate-900">
                                Show {disc.verdict.differences.length} differing title
                                {disc.verdict.differences.length === 1 ? '' : 's'}
                            </summary>
                            <div className="mt-2 overflow-x-auto">
                                <table className="w-full text-xs border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50 text-slate-600">
                                            <th className="border border-slate-200 px-2 py-1 text-left w-12">#</th>
                                            <th className="border border-slate-200 px-2 py-1 text-left">DDP</th>
                                            <th className="border border-slate-200 px-2 py-1 text-left">Booklet</th>
                                            <th className="border border-slate-200 px-2 py-1 text-left w-24">Similarity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {disc.verdict.differences.map((d) => {
                                            const severity = d.severity || 'different';
                                            const rowClass = severity === 'review'
                                                ? 'bg-amber-50/50'
                                                : severity === 'different'
                                                    ? 'bg-red-50/40'
                                                    : '';
                                            const pillClass = severity === 'review'
                                                ? 'bg-amber-100 text-amber-800 border-amber-200'
                                                : severity === 'different'
                                                    ? 'bg-red-100 text-red-800 border-red-200'
                                                    : 'bg-slate-100 text-slate-700 border-slate-200';
                                            return (
                                                <tr key={d.track} className={rowClass}>
                                                    <td className="border border-slate-200 px-2 py-1 text-slate-500 align-top">
                                                        {d.track}
                                                    </td>
                                                    <td className="border border-slate-200 px-2 py-1 text-slate-800 align-top">
                                                        {d.ddp || <span className="italic text-slate-400">— empty —</span>}
                                                    </td>
                                                    <td className="border border-slate-200 px-2 py-1 text-slate-800 align-top">
                                                        {d.booklet || <span className="italic text-slate-400">— empty —</span>}
                                                    </td>
                                                    <td className="border border-slate-200 px-2 py-1 align-top">
                                                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${pillClass}`}>
                                                            {d.similarity != null ? `${d.similarity}%` : '—'}
                                                            {severity === 'review' && ' · review'}
                                                            {severity === 'different' && ' · diff'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </details>
                    )}

                    {/* Manual mapping selector — shown when auto-assignment failed
                         OR when the user wants to reassign a section. Only visible
                         when there are parsed booklet sections to pick from. */}
                    {(showMappingSelector || canRemap) && bookletSections.length > 0 && (
                        <DiscMappingSelector
                            disc={disc}
                            bookletSections={bookletSections}
                            manualMapping={manualMapping}
                            onMap={onMap}
                            disabled={false}
                        />
                    )}

                    {/* Per-disc approval flow (mismatch → customer/admin sign-off) */}
                    {isMismatch && !isApproved && canApprove && (
                        <div className="mt-2" data-testid={`tracklist-disc-approve-section-${disc.disc_number}`}>
                            {!approving ? (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setApproving(true)}
                                    disabled={busy}
                                    className="text-xs border-amber-300 text-amber-800 hover:bg-amber-100"
                                    data-testid={`tracklist-approve-open-btn-${disc.disc_number}`}
                                >
                                    <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
                                    Approve mismatch for {disc.label || `Disc ${disc.disc_number}`}
                                </Button>
                            ) : (
                                <div className="rounded-md border border-amber-200 bg-amber-50/60 p-2 space-y-2">
                                    <label className="text-xs font-medium text-amber-900">
                                        Optional note — why is this mismatch OK?
                                    </label>
                                    <Textarea
                                        rows={2}
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="e.g. Booklet was proof-corrected after DDP was cut. Ship as-is."
                                        data-testid={`tracklist-approve-notes-${disc.disc_number}`}
                                    />
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            onClick={doApprove}
                                            disabled={busy}
                                            data-testid={`tracklist-approve-submit-btn-${disc.disc_number}`}
                                        >
                                            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                                            Confirm approval
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => { setApproving(false); setNotes(''); }}
                                            disabled={busy}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Revoke a prior approval (customer changed their mind or booklet re-uploaded) */}
                    {isApproved && canApprove && (
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {ack.notes && (
                                <span className="text-xs text-slate-600 italic">
                                    Note: {ack.notes}
                                </span>
                            )}
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => onRevokeApproval?.(disc.disc_number)}
                                disabled={busy}
                                className="text-xs text-slate-500 hover:text-red-600 h-7"
                                data-testid={`tracklist-revoke-approval-btn-${disc.disc_number}`}
                            >
                                Undo approval
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ─── Main component ───────────────────────────────────────────────────────

/**
 * TracklistOuterShell
 *
 * Wraps the tracklist validation body in either our own <Card> shell
 * (the legacy layout) or a bare padded div (when the parent already
 * owns a <Card> shell via <CollapsibleProductionSection>). Defined at
 * module scope so React sees a stable component type across renders
 * and never tears down in-flight validation state.
 */
const TracklistOuterShell = ({ embedded, busy, onRerun, children }) => (
    embedded ? (
        <div className="px-6 pb-6 pt-4 space-y-3" data-testid="tracklist-validation-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <p className="text-sm text-slate-500">
                    Automatic DDP master × booklet PDF comparison for each disc.
                </p>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onRerun}
                    disabled={busy}
                    data-testid="tracklist-rerun-btn"
                >
                    {busy ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    ) : (
                        <RefreshCw className="h-4 w-4 mr-1.5" />
                    )}
                    Re-run
                </Button>
            </div>
            {children}
        </div>
    ) : (
        <Card data-testid="tracklist-validation-card">
            <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <ListChecks className="h-5 w-5 text-slate-500" />
                            Tracklist validation
                        </CardTitle>
                        <CardDescription>
                            Automatic DDP master × booklet PDF comparison for each disc.
                        </CardDescription>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onRerun}
                        disabled={busy}
                        data-testid="tracklist-rerun-btn"
                    >
                        {busy ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                        ) : (
                            <RefreshCw className="h-4 w-4 mr-1.5" />
                        )}
                        Re-run
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {children}
            </CardContent>
        </Card>
    )
);

export const TracklistValidationCard = ({ order, orderId, isAdmin, onRefresh, embeddedInCollapsible = false }) => {
    const { user } = useAuth();
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [ackNotes, setAckNotes] = useState('');
    const [showAckForm, setShowAckForm] = useState(false);

    const isCd = order?.product_type === 'cd';

    const fetchResult = useCallback(async () => {
        if (!orderId || !isCd) {
            setLoading(false);
            return;
        }
        try {
            const res = await ordersApi.getTracklistValidation(orderId);
            setResult(res.data);
        } catch (err) {
            // Fail quietly — the card just won't render if no result exists.
            console.warn('Failed to fetch tracklist validation:', err?.message);
        } finally {
            setLoading(false);
        }
    }, [orderId, isCd]);

    useEffect(() => {
        fetchResult();
    }, [fetchResult]);

    const runValidation = async () => {
        setBusy(true);
        try {
            const res = await ordersApi.runTracklistValidation(orderId);
            setResult(res.data);
            toast.success('Validation re-run complete');
            if (onRefresh) onRefresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to run validation');
        } finally {
            setBusy(false);
        }
    };

    const submitAck = async () => {
        setBusy(true);
        try {
            const res = await ordersApi.acknowledgeTracklistValidation(orderId, ackNotes.trim() || null);
            setResult(res.data);
            setShowAckForm(false);
            setAckNotes('');
            toast.success('Validation acknowledged');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to acknowledge');
        } finally {
            setBusy(false);
        }
    };

    const onMapDisc = async (discNumber, sectionIndex) => {
        setBusy(true);
        try {
            const res = await ordersApi.mapTracklistDisc(orderId, discNumber, sectionIndex);
            setResult(res.data);
            toast.success(`Mapped section to Disc ${discNumber}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to map section');
        } finally {
            setBusy(false);
        }
    };

    const onApproveDisc = async (discNumber, notes) => {
        setBusy(true);
        try {
            const res = await ordersApi.approveTracklistDisc(orderId, discNumber, notes);
            setResult(res.data);
            toast.success(`Disc ${discNumber} mismatch approved`);
            if (onRefresh) onRefresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to approve disc');
        } finally {
            setBusy(false);
        }
    };

    const onRevokeApproval = async (discNumber) => {
        setBusy(true);
        try {
            const res = await ordersApi.revokeTracklistDiscApproval(orderId, discNumber);
            setResult(res.data);
            toast.success(`Disc ${discNumber} approval revoked`);
            if (onRefresh) onRefresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to revoke approval');
        } finally {
            setBusy(false);
        }
    };

    // Don't render for non-CD orders at all.
    if (!isCd) return null;
    if (loading) return null;

    // Customer-level policy override: hide the card entirely when the
    // organization has disabled DDP tracklist validation. The backend
    // marks the result with `overall_status === 'disabled'` so we can
    // simply check that field.
    const status = result?.overall_status || 'not_run';
    if (status === 'disabled') return null;

    const hasContent =
        result &&
        (result.discs?.length > 0 ||
            result.booklet_sections?.length > 0 ||
            result.warnings?.length > 0);

    if (status === 'not_run' && !hasContent) {
        if (embeddedInCollapsible) {
            return (
                <div className="px-6 pb-6 pt-4" data-testid="tracklist-validation-card">
                    <p className="text-sm text-slate-500">
                        Once you upload a DDP master and booklet PDF, we&apos;ll automatically
                        check that the track counts and titles match between them.
                    </p>
                </div>
            );
        }
        return (
            <Card data-testid="tracklist-validation-card">
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <ListChecks className="h-5 w-5 text-slate-500" />
                        Tracklist validation
                    </CardTitle>
                    <CardDescription>
                        Once you upload a DDP master and booklet PDF, we&apos;ll automatically
                        check that the track counts and titles match between them.
                    </CardDescription>
                </CardHeader>
            </Card>
        );
    }

    const dupWarnings = result?.duplicate_ddp_warnings || [];
    const genericWarnings = result?.warnings || [];
    const bookletSections = result?.booklet_sections || [];
    const manualMapping = result?.manual_mapping || {};
    const ack = result?.admin_ack;
    const isFailOrWarn = status === 'fail' || status === 'warning';
    const canAcknowledge = isAdmin && isFailOrWarn && !ack?.acknowledged;

    return (
        <TracklistOuterShell
            embedded={embeddedInCollapsible}
            busy={busy}
            onRerun={runValidation}
        >
            <OverallBanner status={status} message={result?.overall_message} ackInfo={ack} />

                {/* Duplicate DDP warnings (two discs with identical hashes) */}
                {dupWarnings.length > 0 && (
                    <div
                        className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
                        data-testid="tracklist-duplicate-warning"
                    >
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                            <div className="text-xs text-amber-900">
                                <p className="font-semibold">Duplicate DDP masters detected</p>
                                <ul className="mt-1 list-disc ml-5">
                                    {dupWarnings.map((w, i) => (
                                        <li key={i}>{w.message}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {/* Generic parse warnings */}
                {genericWarnings.length > 0 && (
                    <div
                        className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
                        data-testid="tracklist-warnings"
                    >
                        <ul className="list-disc ml-5">
                            {genericWarnings.map((w, i) => (
                                <li key={i}>{w}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Per-disc rows */}
                {result?.discs?.length > 0 && (
                    <div className="space-y-2" data-testid="tracklist-discs-list">
                        {result.discs.map((disc) => (
                            <DiscRow
                                key={disc.disc_number}
                                disc={disc}
                                bookletSections={bookletSections}
                                manualMapping={manualMapping}
                                onMap={onMapDisc}
                                canRemap={result.discs.length > 1}
                                onApprove={onApproveDisc}
                                onRevokeApproval={onRevokeApproval}
                                canApprove={!!user}
                                busy={busy}
                            />
                        ))}
                    </div>
                )}

                {/* Admin override / acknowledge flow */}
                {canAcknowledge && (
                    <div
                        className="rounded-md border border-slate-200 bg-slate-50 p-3"
                        data-testid="tracklist-admin-ack-section"
                    >
                        {!showAckForm ? (
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <p className="text-xs text-slate-700 leading-snug">
                                    A mismatch was detected. As an admin, you can override this
                                    warning to allow the order to proceed to production.
                                </p>
                                <Button
                                    size="sm"
                                    variant="default"
                                    onClick={() => setShowAckForm(true)}
                                    data-testid="tracklist-ack-open-btn"
                                >
                                    <ShieldCheck className="h-4 w-4 mr-1.5" />
                                    Acknowledge / Override
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-slate-700">
                                    Optional note (audit trail):
                                </label>
                                <Textarea
                                    rows={2}
                                    value={ackNotes}
                                    onChange={(e) => setAckNotes(e.target.value)}
                                    placeholder="e.g. Customer confirmed booklet was proof-corrected after DDP was made."
                                    data-testid="tracklist-ack-notes"
                                />
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        onClick={submitAck}
                                        disabled={busy}
                                        data-testid="tracklist-ack-submit-btn"
                                    >
                                        {busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
                                        Confirm override
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                            setShowAckForm(false);
                                            setAckNotes('');
                                        }}
                                        disabled={busy}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Timestamp footer */}
                {result?.last_run_at && (
                    <p className="text-[11px] text-slate-400 pt-1">
                        Last checked: {new Date(result.last_run_at).toLocaleString()}
                        {result.last_run_by ? ` — by ${result.last_run_by}` : ''}
                    </p>
                )}
        </TracklistOuterShell>
    );
};

export default TracklistValidationCard;
